import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import api from '../services/apiService';

// Preload hero images for better LCP
const preloadImage = (src: string) => {
  const img = new Image();
  img.src = src;
};

interface HeroProps {
  onScrollToSection: (sectionId: string) => void;
}

interface PromotionSlide {
  id: number;
  type: 'promotion';
  title: string;
  subtitle: string;
  cta: string;
  backgroundImage: string;
}

type CarouselSlide = PromotionSlide;

const Hero: React.FC<HeroProps> = ({ onScrollToSection }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselSlides: CarouselSlide[] = useMemo(() => [
    {
      id: 1,
      type: 'promotion',
      title: t('landing.hero.carousel.slide1.title'),
      subtitle: t('landing.hero.carousel.slide1.subtitle'),
      cta: t('landing.hero.carousel.slide1.cta'),
      backgroundImage: '/optimized/prematch-sports.jpg'
    },
    {
      id: 2,
      type: 'promotion',
      title: t('landing.hero.carousel.slide2.title'),
      subtitle: t('landing.hero.carousel.slide2.subtitle'),
      cta: t('landing.hero.carousel.slide2.cta'),
      backgroundImage: '/optimized/live-sports.jpg'
    },
    {
      id: 3,
      type: 'promotion',
      title: t('landing.hero.carousel.slide3.title'),
      subtitle: t('landing.hero.carousel.slide3.subtitle'),
      cta: t('landing.hero.carousel.slide3.cta'),
      backgroundImage: '/optimized/casino.jpg'
    },
    {
      id: 4,
      type: 'promotion',
      title: t('landing.hero.carousel.slide4.title'),
      subtitle: t('landing.hero.carousel.slide4.subtitle'),
      cta: t('landing.hero.carousel.slide4.cta'),
      backgroundImage: '/optimized/slot.jpg'
    }
  ], [t]);

  const nextSlideIndex = (currentSlide + 1) % carouselSlides.length;

  // Preload only the next slide for smoother transitions
  useEffect(() => {
    // First slide is preloaded in HTML
    if (nextSlideIndex !== 0) {
      preloadImage(carouselSlides[nextSlideIndex].backgroundImage);
    }
  }, [carouselSlides, currentSlide, nextSlideIndex]);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [carouselSlides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselSlides.length) % carouselSlides.length);
  };

  return (
    <section id="home" className="pt-3 pb-6 sm:pt-4 sm:pb-8 lg:pt-6 lg:pb-12 px-3 sm:px-4 lg:px-8 bg-gradient-to-b from-dark-900 to-dark-800 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        {/* Hero Carousel */}
        <div className="relative mb-4 sm:mb-6 lg:mb-8">
          <div className="relative h-48 sm:h-64 md:h-80 lg:h-[420px] overflow-hidden rounded-xl sm:rounded-2xl shadow-2xl">
            {carouselSlides.map((slide, index) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-all duration-700 ease-out ${
                  index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105 pointer-events-none'
                }`}
                aria-hidden={index !== currentSlide}
              >
                <div className="h-full rounded-xl sm:rounded-2xl relative overflow-hidden" style={{ contain: 'layout paint' }}>
                  {/* Use img element for better LCP optimization */}
                  {(index === currentSlide || index === nextSlideIndex) && (
                    <img
                      src={slide.backgroundImage}
                      alt={slide.title}
                      width={1200}
                      height={400}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading={index === currentSlide ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchPriority={index === currentSlide ? 'high' : 'auto'}
                      sizes="(max-width: 768px) 100vw, 1200px"
                    />
                  )}
                  {/* Premium Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-dark-900/90 via-dark-900/40 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 via-transparent to-transparent"></div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-10 sm:-top-20 -right-10 sm:-right-20 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
                    <div className="absolute -bottom-10 sm:-bottom-20 -left-10 sm:-left-20 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bg-gold-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
                  </div>

                  <div className="relative z-10 h-full p-4 sm:p-6 md:p-8 lg:p-12 flex items-center">
                    <div className="max-w-2xl">
                      <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-0.5 sm:py-1 bg-primary-500/20 border border-primary-500/30 rounded-full text-primary-400 text-[10px] sm:text-xs font-semibold mb-2 sm:mb-4 backdrop-blur-sm">
                        <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-primary-500 rounded-full animate-pulse"></span>
                        {t('landing.hero.brandName')}
                      </div>
                      <h1 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white mb-2 sm:mb-4 leading-[1.1] tracking-tight">
                        {slide.title}
                      </h1>
                      <p className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-lg mb-3 sm:mb-6 lg:mb-8 max-w-xl leading-relaxed line-clamp-2 sm:line-clamp-none">
                        {slide.subtitle}
                      </p>
                      <div className="flex flex-wrap gap-2 sm:gap-3">
                        <button 
                          onClick={() => onScrollToSection('seo-content')}
                          className="group relative overflow-hidden bg-gradient-to-r from-primary-600 to-emerald-500 text-white font-bold text-xs sm:text-sm md:text-base px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 rounded-lg sm:rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:scale-[1.02]"
                        >
                          <span className="relative z-10 flex items-center gap-1.5 sm:gap-2">
                            {slide.cta}
                            <svg className="w-3 sm:w-4 h-3 sm:h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                        </button>
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Carousel Navigation */}
          <button
            onClick={prevSlide}
            aria-label="Previous slide"
            className="absolute left-2 sm:left-3 md:left-5 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-dark-900/80 hover:bg-dark-800 rounded-lg sm:rounded-xl flex items-center justify-center text-white transition-all duration-200 shadow-lg backdrop-blur-md border border-white/10 hover:border-white/20 hover:scale-105"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            aria-label="Next slide"
            className="absolute right-2 sm:right-3 md:right-5 top-1/2 transform -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-dark-900/80 hover:bg-dark-800 rounded-lg sm:rounded-xl flex items-center justify-center text-white transition-all duration-200 shadow-lg backdrop-blur-md border border-white/10 hover:border-white/20 hover:scale-105"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Carousel Indicators - Modern Style */}
          <div className="absolute bottom-2 sm:bottom-4 md:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-1.5 sm:gap-2 bg-dark-900/60 backdrop-blur-md px-2 sm:px-4 py-1 sm:py-2 rounded-full" role="tablist" aria-label="Carousel slides">
            {carouselSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                role="tab"
                aria-selected={index === currentSlide}
                aria-label={`Go to slide ${index + 1}`}
                className={`transition-all duration-300 rounded-full ${
                  index === currentSlide 
                    ? 'w-5 sm:w-8 h-1.5 sm:h-2 bg-primary-500' 
                    : 'w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Top Wagering Players Section */}
        <div className="mb-6 sm:mb-8 lg:mb-10">
          <TopWageringPlayers />
        </div>
      </div>
    </section>
  );
};

// Top Wagering Players Component
const TopWageringPlayers: React.FC = () => {
  const { t } = useTranslation();
  
  const { data, isLoading } = useQuery({
    queryKey: ['topWageringPlayers'],
    queryFn: async () => {
      const response = await api.getRaw('/api/Users/getTopWageringPlayers');
      return response;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });

  const players = data?.data || [];
  // VIP level icon (matches WalletPage / vip_levels)
  const levelIcons: Record<number, string> = {
    1: '🥉',
    2: '🥈',
    3: '🥇',
    4: '💎',
    5: '👑',
    6: '⭐',
  };
  const getLevelIcon = (level: number) => levelIcons[Math.min(level, 6)] || '🥉';
  const colors = [
    { color: 'from-gold-500/20 to-gold-600/10', border: 'border-gold-500/20' },
    { color: 'from-primary-500/20 to-primary-600/10', border: 'border-primary-500/20' },
    { color: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/20' },
    { color: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/20' },
    { color: 'from-pink-500/20 to-pink-600/10', border: 'border-pink-500/20' },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-5">
          <div className="flex items-center gap-1.5 sm:gap-2">
            <span className="text-lg sm:text-2xl">🏆</span>
            <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white">{t('landing.hero.bigWagering', { defaultValue: 'Big Wagering Players' })}</h2>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-primary-500/50 to-transparent"></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-dark-700/50 rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 animate-pulse">
              <div className="h-16 bg-dark-600/50 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (players.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-5">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <span className="text-lg sm:text-2xl">🏆</span>
          <h2 className="text-base sm:text-xl md:text-2xl font-bold text-white">{t('landing.hero.bigWagering', { defaultValue: 'Big Wagering Players' })}</h2>
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-primary-500/50 to-transparent"></div>
        <div className="flex items-center gap-1 text-primary-400 text-[10px] sm:text-sm font-medium">
          <span className="w-1.5 sm:w-2 h-1.5 sm:h-2 bg-primary-500 rounded-full animate-pulse"></span>
          LIVE
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
        {players.map((player: any, index: number) => {
          const colorScheme = colors[index % colors.length];
          return (
            <div key={index} className={`relative bg-gradient-to-br ${colorScheme.color} backdrop-blur-sm border ${colorScheme.border} rounded-lg sm:rounded-xl p-2 sm:p-3 md:p-4 hover:scale-[1.02] transition-all duration-300 group cursor-pointer`}>
              {/* Level badge - right top of card */}
              <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex items-center gap-0.5 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md" title={`Level ${player.level ?? 1}`}>
                <span className="text-sm sm:text-base leading-none" aria-hidden>{getLevelIcon(player.level ?? 1)}</span>
                <span className="text-[10px] sm:text-xs font-semibold text-white">Lv.{player.level ?? 1}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {player.avatar ? (
                    <img 
                      src={player.avatar} 
                      alt={player.player}
                      className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border-2 border-white/20 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(player.player)}&background=random&color=fff&size=128&bold=true`;
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-primary-500 to-emerald-500 flex items-center justify-center text-white font-bold text-xs sm:text-sm md:text-base border-2 border-white/20">
                      {player.player.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[10px] sm:text-xs md:text-sm font-medium truncate">{player.player}</div>
                  <div className="text-gray-400 text-[8px] sm:text-[10px] truncate">Total Wagering</div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-[8px] sm:text-xs">Wagered</span>
                <span className="text-gold-400 font-bold text-xs sm:text-sm md:text-base">{formatCurrency(player.totalWagering)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Hero; 